import { useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setSideBarVisibility } from "context";

// Images
import logo from "assets/images/vct_technologies_logo.png";
import PublicDocsLanding from "layouts/landing/publicDocsLanding";
import RequestDataDelete from "layouts/landing/components/RequestDataDelete";
import TermsOfUse from "layouts/landing/termsOfUse";
import PrivacyPolicy from "layouts/landing/privacyPolicy";
import { useDispatch, useSelector } from "react-redux";
import { PersistLoggedInUser, IsAuthenticated } from "./helpers";
import {
  unauthenticatedRoutes,
  customerRoutes,
  rmRoutes,
  adminRoutes,
  supportRoutes,
  techRoutes,
} from "routes";
import "devextreme/dist/css/dx.light.css";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { user_data } = useSelector((state) => state.authentication);

  let routes = customerRoutes;

  const { direction, layout, sidenavColor, darkMode } = controller;
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Authentication Check
  useEffect(() => {
    const tokenValidity = localStorage.getItem("token_validity");
    const TokenDate = new Date(tokenValidity);
    const NewDate = new Date();
    if (tokenValidity != null && TokenDate <= NewDate) {
      localStorage.clear();
      navigate("/");
      // console.log(tokenValidity);
    }
  }, []);
  //Hiding the sidebar when navigate to the home page

  const dispatchData = useDispatch();
  useEffect(() => {
    setSideBarVisibility(dispatch, false);
    PersistLoggedInUser(dispatchData);
  }, []);

  if (user_data?.customer?.roleId == 2) {
    routes = rmRoutes;
  } else if (user_data?.customer?.roleId == 3) {
    routes = supportRoutes;
  } else if (user_data?.customer?.roleId == 4) {
    routes = adminRoutes;
  } else if (user_data?.customer?.roleId == 5) {
    routes = techRoutes;
  } else {
    routes = customerRoutes;
  }

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  const url = window.location.pathname;
  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />

      {IsAuthenticated() && url !== "/authentication/sign-in"
        ? layout === "dashboard" && (
            <Sidenav
              color={sidenavColor}
              brand={logo}
              brandName="Gato Systems"
              routes={routes}
              onMouseEnter={() => {}}
              onMouseLeave={() => {}}
            />
          )
        : ""}
      {/* {layout === "vr" && <Configurator />} */}

      {!IsAuthenticated() ? (
        <Routes>
          {getRoutes(unauthenticatedRoutes)}
          <Route path="/" element={<PublicDocsLanding api="account-opening" />} key="public-docs-home" />
          <Route path="/docs/funding" element={<PublicDocsLanding api="funding" />} key="public-docs-funding" />
          <Route path="/docs/trading" element={<PublicDocsLanding api="trading" />} key="public-docs-trading" />
          <Route path="/docs/trading/v2.5.1" element={<PublicDocsLanding api="trading-v251" />} key="public-docs-trading-v251" />
          <Route path="/docs/trading/v2.5" element={<PublicDocsLanding api="trading-v25" />} key="public-docs-trading-v25" />
          <Route path="/docs/trading/v2.4" element={<PublicDocsLanding api="trading-v24" />} key="public-docs-trading-v24" />
          <Route path="/docs/trading/v2.3" element={<PublicDocsLanding api="trading-v23" />} key="public-docs-trading-v23" />
          <Route path="/docs/trading/v2.2.4" element={<PublicDocsLanding api="trading-v224" />} key="public-docs-trading-v224" />
          <Route path="/docs/fix" element={<PublicDocsLanding api="fix" />} key="public-docs-fix" />
          <Route path="/docs/fix/v3.2" element={<PublicDocsLanding api="fix-v32" />} key="public-docs-fix-v32" />
          <Route path="/docs/corporate-actions-api" element={<PublicDocsLanding api="gflux" />} key="public-docs-gflux" />
          <Route path="/docs/risk-margin-api" element={<PublicDocsLanding api="risk-margin-api" />} key="public-docs-risk-margin-api" />
          <Route path="/termsofuse" element={<TermsOfUse />} key="termsofuse" />
          <Route
            path="/privacypolicy"
            element={<PrivacyPolicy />}
            key="privacypolicy"
          />
          <Route
            path="/request-data-delete"
            element={<RequestDataDelete />}
            key="request-data-delete"
          />
          <Route path="*" element={<Navigate to="/authentication/sign-in" replace />} key="home" />
        </Routes>
      ) : (
        <Routes>
          {getRoutes(routes)}
          <Route path="/" element={<PublicDocsLanding api="account-opening" />} key="public-docs-home" />
          <Route path="/docs/funding" element={<PublicDocsLanding api="funding" />} key="public-docs-funding" />
          <Route path="/docs/trading" element={<PublicDocsLanding api="trading" />} key="public-docs-trading" />
          <Route path="/docs/trading/v2.5.1" element={<PublicDocsLanding api="trading-v251" />} key="public-docs-trading-v251" />
          <Route path="/docs/trading/v2.5" element={<PublicDocsLanding api="trading-v25" />} key="public-docs-trading-v25" />
          <Route path="/docs/trading/v2.4" element={<PublicDocsLanding api="trading-v24" />} key="public-docs-trading-v24" />
          <Route path="/docs/trading/v2.3" element={<PublicDocsLanding api="trading-v23" />} key="public-docs-trading-v23" />
          <Route path="/docs/trading/v2.2.4" element={<PublicDocsLanding api="trading-v224" />} key="public-docs-trading-v224" />
          <Route path="/docs/fix" element={<PublicDocsLanding api="fix" />} key="public-docs-fix" />
          <Route path="/docs/fix/v3.2" element={<PublicDocsLanding api="fix-v32" />} key="public-docs-fix-v32" />
          <Route path="/docs/corporate-actions-api" element={<PublicDocsLanding api="gflux" />} key="public-docs-corporate-actions-api" />
          <Route path="/docs/risk-margin-api" element={<PublicDocsLanding api="risk-margin-api" />} key="public-docs-risk-margin-api-v2" />
          <Route path="/termsofuse" element={<TermsOfUse />} key="termsofuse" />
          <Route
            path="/privacypolicy"
            element={<PrivacyPolicy />}
            key="privacypolicy"
          />
          <Route
            path="/request-data-delete"
            element={<RequestDataDelete />}
            key="request-data-delete"
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} key="home" />
        </Routes>
      )}
    </ThemeProvider>
  );
}

